import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { api, type TipoPallet } from '../api';
import { mensajeDeError, useApi, VIVO } from '../lib/useApi';
import { useSesion } from '../lib/sesion';
import { useEsCelular } from '../lib/useMedia';
import { abrir, desplegar, fila, resorte } from '../components/movimiento';
import {
  Buscador,
  Cargando,
  Encabezado,
  EstadoChip,
  filtrar,
  Icono,
  Kpi,
  Paginador,
  usePaginado,
  PanelError,
  useConfirmar,
  Vacio,
} from '../components/ui';
import {
  altoMinimo,
  anchoDeCampo,
  botonPrimario,
  botonSecundario,
  c,
  campo,
  encabezadoTabla,
  enlace,
  etiquetaCampo,
  filaDeCampos,
  filaTabla,
  miles,
  mono,
  tarjeta,
} from '../theme';

/**
 * Armado de pallets: el paso del medio del circuito.
 *
 * Los pallets llegan armados y sin ningún dato de qué tienen arriba. El
 * operario los abre, los mira, y va declarando qué items del stock que cargó
 * ZF hay en cada uno. Un pallet puede llevar varios artículos, así que se
 * carga línea por línea.
 *
 * Cada línea descuenta del pendiente de ese renglón de stock: es lo que
 * impide que la misma mercadería termine declarada arriba de dos pallets.
 *
 * El pallet nace sin ubicar. Asignarle posición es el paso siguiente y se
 * hace en Ubicaciones, que es donde está el plano.
 *
 * El código **no se escribe**: lo pone el sistema siguiendo la numeración que
 * ya hay. Antes al de fraccionamiento se lo copiaba de la etiqueta que traía el
 * pallet, y eso era veinte códigos tipeados por contenedor, cada uno una
 * oportunidad de equivocarse. Y se arma de a tandas —tantos pallets de tantas
 * piezas cada uno— porque un contenedor baja veinte iguales del mismo material.
 *
 * Para un pallet mezclado, cada uno de los que están abajo tiene «Sumarle otro
 * item», que apunta este formulario a ése.
 *
 * Acá también se decide de qué clase es, y es lo único que no se puede
 * corregir después: el pallet que **se fracciona** se abre más tarde en la
 * mesa y salen capachos de a poco; el que **sale completo** es el que ZF pide
 * entero, así que lleva un solo renglón.
 *
 * Y de qué contenedor bajó. Al pallet que entra por recepción se lo pone el
 * sistema; a éste no, porque el stock que carga ZF no dice de qué contenedor
 * vino. Lo elige el operario, que es el que lo vio bajar.
 */
export default function Armado({
  onUbicar,
  onVerContenedor,
}: {
  onUbicar: (pallet?: string) => void;
  onVerContenedor: (contenedor: string) => void;
}) {
  const { vistas } = useSesion();
  const celular = useEsCelular();
  // El stock que sube el depósito y los pallets que arma otro operario: dos
  // manos sobre lo mismo, así que la pantalla se mantiene sola.
  const { datos, cargando, error, recargar } = useApi(() => api.armado(), [], VIVO.normal);

  const [tipo, setTipo] = useState<TipoPallet>('fraccionamiento');
  const [contenedor, setContenedor] = useState('');
  const [item, setItem] = useState('');
  const [piezas, setPiezas] = useState('');
  // Si el operario ya escribió las piezas a mano: mientras no lo haga, se le
  // precarga la cuenta y no se la pisa.
  const [piezasTocado, setPiezasTocado] = useState(false);
  const [cuantos, setCuantos] = useState('1');
  // A qué pallet ya armado se le está sumando un artículo. Null es el caso
  // normal: armar pallets nuevos.
  const [sumandoA, setSumandoA] = useState<string | null>(null);
  const [aviso, setAviso] = useState('');
  const [fallo, setFallo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [confirmar, dialogoConfirmar] = useConfirmar();

  const pendientes = datos?.pendientes ?? [];
  const pallets = datos?.pallets ?? [];
  const [busqueda, setBusqueda] = useState('');
  const encontrados = filtrar(pallets, busqueda, (p) => [
    p.id,
    p.contenedor,
    ...p.items.map((it) => `${it.articulo} ${it.descripcion} ${it.lote ?? ''}`),
  ]);
  const pagina = usePaginado(encontrados);
  const contenedores = datos?.contenedores ?? [];

  const elegido = useMemo(() => pendientes.find((p) => String(p.id) === item), [pendientes, item]);
  const cantidad = Math.trunc(Number(piezas));
  // Cuántos pallets se arman de una. Sumándole un artículo a uno que ya existe
  // es siempre uno: el que se tiene delante.
  const tanda = sumandoA ? 1 : Math.trunc(Number(cuantos));
  const completo = tipo === 'completo';

  // Con un contenedor elegido, sólo tiene sentido ofrecer lo que bajó de ése.
  const pendientesFiltrados = contenedor ? pendientes.filter((p) => p.contenedor === contenedor) : pendientes;

  // Cuánto precargar en «Piezas por pallet»: lo que queda del renglón,
  // repartido entre los pallets que se están armando. Redondeado para abajo y
  // nunca cero, porque cero no arma nada.
  function piezasPrecargadas(pendiente: number, cuantosPallets: string) {
    const n = Math.max(1, Math.trunc(Number(cuantosPallets)) || 1);
    return String(Math.max(1, Math.floor(pendiente / n)));
  }

  function alElegirItem(id: string) {
    setItem(id);
    setPiezasTocado(false);
    const p = pendientes.find((x) => String(x.id) === id);
    // El renglón pendiente ya sabe de qué contenedor bajó: se lo completa acá
    // para no hacérselo elegir dos veces.
    if (p?.contenedor) setContenedor(p.contenedor);
    // Y con qué clase se declaró al cargar el contenedor: la clase es lo único
    // que no se corrige después, así que se arranca con la del papel y no con
    // «Se fracciona» por defecto. Los renglones viejos no la tienen.
    if (p?.tipo) setTipo(p.tipo);
    setPiezas(p ? piezasPrecargadas(p.pendiente, sumandoA ? '1' : cuantos) : '');
  }

  async function alCambiarTipo(valor: TipoPallet) {
    // Ir contra la clase declarada puede ser lo correcto —el operario tiene el
    // pallet delante— pero no se corrige después: se pregunta antes.
    if (elegido?.tipo && valor !== elegido.tipo) {
      const fraccionar = valor === 'fraccionamiento';
      const ok = await confirmar({
        titulo: fraccionar
          ? 'Este renglón se cargó como entero. ¿Armarlo para fraccionar?'
          : 'Este renglón se cargó para fraccionar. ¿Armarlo entero?',
        detalle: 'Es la clase que figura al cargar el contenedor, y no se cambia después de armar el pallet.',
        boton: fraccionar ? 'Armarlo para fraccionar' : 'Armarlo entero',
      });
      if (!ok) return;
    }
    setTipo(valor);
  }

  function alCambiarCuantos(valor: string) {
    setCuantos(valor);
    if (!piezasTocado && elegido) setPiezas(piezasPrecargadas(elegido.pendiente, valor));
  }

  function alElegirContenedor(valor: string) {
    setContenedor(valor);
    // Si el artículo ya elegido no es de este contenedor, se limpia: la lista
    // de abajo lo va a dejar afuera.
    if (valor && elegido && elegido.contenedor !== valor) {
      setItem('');
      setPiezas('');
      setPiezasTocado(false);
    }
  }

  async function quitarLinea(id: number, pallet: (typeof pallets)[number]) {
    if (
      pallet.items.length === 1 &&
      !(await confirmar({
        titulo: `¿Borrar el pallet ${pallet.id}?`,
        detalle: 'Es la única línea que tiene: sacarla borra el pallet entero.',
        boton: `Borrar el pallet ${pallet.id}`,
      }))
    )
      return;
    await quitar(id);
  }
  const puedeAgregar =
    !ocupado &&
    Boolean(elegido) &&
    Number.isFinite(cantidad) &&
    cantidad > 0 &&
    Number.isFinite(tanda) &&
    tanda > 0 &&
    // La tanda entera tiene que entrar en lo que queda sin paletizar. El
    // servidor lo vuelve a revisar bajo lock, pero avisar antes evita mandar
    // algo que ya se sabe que va a rebotar.
    cantidad * tanda <= (elegido?.pendiente ?? 0);

  async function agregar() {
    if (!puedeAgregar || !elegido) return;
    setOcupado(true);
    setFallo('');
    try {
      const r = await api.agregarLinea({
        ...(sumandoA ? { pallet: sumandoA } : { cantidad: tanda }),
        stockZfId: elegido.id,
        piezas: cantidad,
        tipo,
        contenedor,
      });
      const piezasDe = `${miles(cantidad)} piezas de ${elegido.articulo}`;
      setAviso(
        !r.nuevo
          ? `${piezasDe} se sumaron a ${r.pallets[0]}.`
          : r.cantidad === 1
            ? `Se armó ${r.pallets[0]} con ${piezasDe}.${r.tipo === 'completo' ? ' Sale completo: ya está cerrado.' : ''}`
            : `Se armaron ${r.cantidad} pallets de ${piezasDe} cada uno, de ${r.pallets[0]} a ${r.pallets[r.cantidad - 1]}.`,
      );
      setItem('');
      setPiezas('');
      setPiezasTocado(false);
      setCuantos('1');
      setSumandoA(null);
      recargar();
    } catch (err) {
      setFallo(mensajeDeError(err));
    } finally {
      setOcupado(false);
    }
  }

  async function quitar(id: number) {
    setFallo('');
    try {
      const r = await api.quitarLinea(id);
      setAviso(r.palletBorrado ? `${r.pallet} se quedó sin contenido y se borró.` : `Se quitó una línea de ${r.pallet}.`);
      recargar();
    } catch (err) {
      setFallo(mensajeDeError(err));
    }
  }

  const piezasPendientes = pendientes.reduce((a, p) => a + p.pendiente, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1100 }}>
      <Encabezado
        bajada="Cuando el depósito carga un contenedor, sus pallets entran en 0. Acá se declara qué artículos tiene arriba cada uno."
      >
        <button type="button" onClick={recargar} style={botonSecundario}>
          <Icono nombre="refresh" size={19} />
          Actualizar
        </button>
      </Encabezado>

      {error && <PanelError mensaje={error} onReintentar={recargar} />}
      {fallo && <PanelError mensaje={fallo} />}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 16 }}>
        <Kpi
          rotulo="Pendiente de armar"
          valor={piezasPendientes}
          pie={`${pendientes.length} ${pendientes.length === 1 ? 'línea' : 'líneas'} sin armar`}
          color={piezasPendientes ? c.texto : c.verde}
        />
        <div>
          <Kpi
            rotulo="Pallets sin ubicar"
            valor={pallets.length}
            pie={pallets.length ? 'les falta posición' : 'ninguno pendiente'}
          />
          {pallets.length > 0 && vistas.includes('ubicar') && (
            <button type="button" onClick={() => onUbicar()} style={celular ? { ...botonSecundario, width: '100%', justifyContent: 'center', marginTop: 8 } : { ...enlace, fontSize: 13, marginTop: 8 }}>
              Ubicarlos ahora
            </button>
          )}
        </div>
        <Kpi
          rotulo="Piezas ya arriba"
          valor={pallets.reduce((a, p) => a + p.piezas, 0)}
          pie="en los pallets sin ubicar"
        />
      </div>

      {/* ------------------------------------------------ cargar una línea */}

      <div style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: c.texto }}>
          {sumandoA ? 'Sumarle un artículo a un pallet armado' : 'Armar pallets'}
        </span>

        {/* Sin esto, tocar «Sumarle otro item» abajo y volver arriba deja un
            formulario que se ve igual que el de armar, y lo que se cargue va a
            un lado distinto del que el operario cree. */}
        <AnimatePresence initial={false}>
        {sumandoA && (
          <motion.div {...abrir(8)} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13, color: c.texto2 }}>
            <Icono nombre="add" size={17} color={c.verde} />
            <span>
              Lo que cargues se suma al pallet <span style={{ fontFamily: mono, color: c.texto }}>{sumandoA}</span>, no
              arma uno nuevo.
            </span>
            <button
              type="button"
              onClick={() => setSumandoA(null)}
              style={{ ...botonSecundario, minHeight: altoMinimo(32), padding: '5px 12px' }}
            >
              Armar pallets nuevos
            </button>
          </motion.div>
        )}
        </AnimatePresence>

        <div style={filaDeCampos}>
          {/* La clase se elige al armar. Sumándole un artículo a uno que ya
              existe, la clase es la que ese pallet ya tiene. */}
          {!sumandoA && (
          <label style={{ ...etiquetaCampo, ...anchoDeCampo(180) }}>
            Clase del pallet
            <select value={tipo} onChange={(e) => void alCambiarTipo(e.target.value as TipoPallet)} style={campo}>
              <option value="fraccionamiento">Se fracciona (capachos)</option>
              <option value="completo">Sale completo</option>
            </select>
          </label>
          )}

          <label style={{ ...etiquetaCampo, ...anchoDeCampo(170) }}>
            Contenedor
            <select value={contenedor} onChange={(e) => alElegirContenedor(e.target.value)} style={campo}>
              <option value="">Sin contenedor</option>
              {contenedores.map((ct) => (
                <option key={ct.id} value={ct.id}>
                  {ct.id} · {ct.fecha}
                </option>
              ))}
            </select>
          </label>

          <label style={{ ...etiquetaCampo, ...anchoDeCampo(240) }}>
            Lo que declaró el contenedor
            <select value={item} onChange={(e) => alElegirItem(e.target.value)} style={campo}>
              <option value="">Elegí qué hay arriba…</option>
              {pendientesFiltrados.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.articulo}
                  {p.descripcion && p.descripcion !== p.articulo ? ` · ${p.descripcion}` : ''}
                  {p.lote ? ` · ${p.lote}` : ''} — quedan {p.pendiente}
                </option>
              ))}
            </select>
          </label>

          <label style={{ ...etiquetaCampo, ...anchoDeCampo(130) }}>
            Piezas por pallet
            <input
              value={piezas}
              onChange={(e) => {
                setPiezas(e.target.value);
                setPiezasTocado(true);
              }}
              inputMode="numeric"
              placeholder={elegido ? String(elegido.pendiente) : '0'}
              style={campo}
            />
          </label>

          {/* Un contenedor baja veinte pallets iguales del mismo material.
              Cargarlos de a uno era escribir veinte veces lo mismo. Sumándole
              un artículo a un pallet que ya existe no va: es uno solo. */}
          {!sumandoA && (
            <label style={{ ...etiquetaCampo, ...anchoDeCampo(120) }}>
              Cuántos pallets
              <input
                value={cuantos}
                onChange={(e) => alCambiarCuantos(e.target.value)}
                inputMode="numeric"
                placeholder="1"
                style={campo}
              />
            </label>
          )}

          <button
            type="button"
            onClick={agregar}
            disabled={!puedeAgregar}
            className="w-full justify-center tablet:w-auto"
            style={{ ...botonPrimario, flex: '0 0 auto', opacity: puedeAgregar ? 1 : 0.5 }}
          >
            <Icono nombre="add" size={19} />
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={ocupado ? 'cargando' : sumandoA ? `sumar-${sumandoA}` : tanda > 1 ? `armar-${tanda}` : 'armar'}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.14 }}
              >
                {ocupado
                  ? 'Cargando…'
                  : sumandoA
                    ? `Sumar a ${sumandoA}`
                    : tanda > 1
                      ? `Armar ${tanda} pallets`
                      : 'Armar el pallet'}
              </motion.span>
            </AnimatePresence>
          </button>
        </div>

        <AnimatePresence mode="wait" initial={false}>
        {elegido && (
          <motion.div key={elegido.id} {...abrir(8)} style={{ fontSize: 13, color: c.texto2 }}>
            De <strong>{elegido.articulo}</strong> quedan <strong>{miles(elegido.pendiente)}</strong> piezas sin
            paletizar, de {miles(elegido.piezas)} que declaró el contenedor.
            {tanda > 1 && Number.isFinite(cantidad) && cantidad > 0 && (
              <>
                {' '}
                {tanda} pallets de {miles(cantidad)} son{' '}
                <strong style={{ color: cantidad * tanda > elegido.pendiente ? c.rojo : c.texto }}>
                  {miles(cantidad * tanda)}
                </strong>
                {cantidad * tanda > elegido.pendiente ? ': no alcanza.' : '.'}
              </>
            )}
          </motion.div>
        )}
        </AnimatePresence>

        <AnimatePresence initial={false}>
          {completo && !sumandoA && (
            <motion.div {...desplegar} style={{ ...desplegar.style, fontSize: 13, color: c.texto3 }}>
              Un pallet completo lleva un solo renglón: se arma con esta carga y queda cerrado. ZF lo va a pedir
              entero, y al despacharlo sale con todo lo que tiene arriba.
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {aviso && (
            <motion.div
              key={aviso}
              role="status"
              {...abrir(8)}
              style={{ fontSize: 13, color: c.verde, display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <Icono nombre="task_alt" size={17} color={c.verde} />
              {aviso}
            </motion.div>
          )}
        </AnimatePresence>

        {!cargando && pendientes.length === 0 && (
          <div style={{ fontSize: 13, color: c.texto3 }}>
            No queda nada pendiente de armar. Cuando se cargue un contenedor con pallets en 0, aparece acá.
          </div>
        )}
      </div>

      {/* ---------------------------------------- los pallets que se armaron */}

      <div style={{ ...tarjeta, overflow: 'hidden', position: 'relative' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 16px',
            borderBottom: `1px solid ${c.borde}`,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 600, color: c.texto }}>Pallets armados sin ubicar</span>
          <span style={{ flex: '1 1 220px', display: 'flex', justifyContent: 'flex-end' }}>
            {pallets.length > 0 && (
              <Buscador valor={busqueda} onCambiar={setBusqueda} placeholder="Pallet, artículo o contenedor" ancho={320} />
            )}
          </span>
          {pallets.length > 0 && <EstadoChip estado={`${pallets.length} sin posición`} tono="warn" />}
        </div>

        {cargando && !datos ? (
          <Cargando texto="Buscando lo que hay en el muelle…" />
        ) : pallets.length === 0 ? (
          <Vacio
            icono="pallet"
            titulo="Ningún pallet a medio armar"
            detalle="Cargá un item arriba de un pallet y aparece acá hasta que le asignes posición."
          />
        ) : (
          <AnimatePresence initial={false} mode="popLayout">
          {pagina.visibles.map((p, i) => (
            <motion.div key={p.id} layout {...fila(i)} transition={{ ...fila(i).transition, layout: resorte }} style={{ borderBottom: `1px solid ${c.borde}` }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  background: c.fondo,
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ fontFamily: mono, fontSize: 14, fontWeight: 600, color: c.texto }}>{p.id}</span>
                {p.tipo === 'completo' && <EstadoChip estado="sale completo" tono="ok" />}
                <span className="basis-full tablet:basis-auto" style={{ fontSize: 12.5, color: c.texto3 }}>
                  {p.items.length} {p.items.length === 1 ? 'artículo' : 'artículos'} · {miles(p.piezas)} piezas
                  {p.contenedor ? ' · ' : ''}
                  {p.contenedor &&
                    (vistas.includes('contenedor') ? (
                      <button type="button" onClick={() => onVerContenedor(p.contenedor!)} title="Ver la ficha del contenedor" style={{ ...enlace, fontSize: 12.5 }}>
                        {p.contenedor}
                      </button>
                    ) : (
                      <span style={{ fontFamily: mono }}>{p.contenedor}</span>
                    ))}
                </span>
                <span className="hidden tablet:block" style={{ flex: 1 }} />
                {p.tipo !== 'completo' && (
                  <button
                    type="button"
                    onClick={() => {
                      // Apunta el formulario de arriba a este pallet: es como
                      // se arma uno mezclado ahora que el código lo pone el
                      // sistema y no hay dónde escribirlo.
                      setSumandoA(p.id);
                      setItem('');
                      setPiezas('');
                      setPiezasTocado(false);
                      setAviso('');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="flex-[3] whitespace-nowrap tablet:flex-none"
                    style={{
                      ...botonSecundario,
                      padding: '7px 14px',
                      justifyContent: 'center',
                      ...(sumandoA === p.id ? { borderColor: c.verde, background: c.verdeSuave, color: c.verde } : {}),
                    }}
                  >
                    <Icono nombre="add" size={16} />
                    Sumarle otro item
                  </button>
                )}
                <button type="button" onClick={() => onUbicar(p.id)} className="flex-[2] tablet:flex-none" style={{ ...botonPrimario, minHeight: altoMinimo(36), padding: '7px 14px', justifyContent: 'center' }}>
                  <Icono nombre="grid_view" size={16} />
                  Ubicar
                </button>
              </div>

              {celular ? (
                // En el celular cada artículo es una tarjetita: el código y las
                // piezas arriba, la descripción y el FIFO debajo.
                <AnimatePresence initial={false} mode="popLayout">
                  {p.items.map((it, j) => (
                    <motion.div
                      key={it.id}
                      layout
                      {...fila(j)}
                      style={{ ...filaTabla, alignItems: 'flex-start', gap: 10, borderBottom: j < p.items.length - 1 ? filaTabla.borderBottom : 'none' }}
                    >
                      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                          <span style={{ fontFamily: mono, fontSize: 14, fontWeight: 600 }}>{it.articulo}</span>
                          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{miles(it.piezas)} pzs</span>
                        </span>
                        <span style={{ color: c.texto2 }}>{it.descripcion}</span>
                        {it.lote && (
                          <span style={{ fontFamily: mono, fontSize: 12.5, color: c.texto3 }}>FIFO {it.lote}</span>
                        )}
                      </span>
                      <button
                        type="button"
                        title="Quitar la línea"
                        aria-label={`Quitar ${it.articulo}`}
                        onClick={() => void quitarLinea(it.id, p)}
                        style={{ ...botonSecundario, width: 44, minHeight: 44, padding: 0, borderRadius: 999, flex: '0 0 44px' }}
                      >
                        <Icono nombre="close" size={16} />
                      </button>
                    </motion.div>
                  ))}
                </AnimatePresence>
              ) : (
              <div className="wms-scroll-x">
                <div>
                  <div style={encabezadoTabla}>
                    <span style={{ width: 140 }}>Artículo</span>
                    <span style={{ flex: 1, minWidth: 180 }}>Descripción</span>
                    <span style={{ width: 110 }}>FIFO</span>
                    <span style={{ width: 100, textAlign: 'right' }}>Piezas</span>
                    <span style={{ width: 44 }} />
                  </div>
                  <AnimatePresence initial={false} mode="popLayout">
                  {p.items.map((it, j) => (
                    <motion.div key={it.id} layout {...fila(j)} style={filaTabla}>
                      <span style={{ width: 140, fontFamily: mono, fontSize: 13 }}>{it.articulo}</span>
                      <span style={{ flex: 1, minWidth: 180, color: c.texto2 }}>{it.descripcion}</span>
                      <span style={{ width: 110, fontFamily: mono, fontSize: 12.5, color: c.texto2 }}>
                        {it.lote ?? '—'}
                      </span>
                      <span
                        style={{ width: 100, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}
                      >
                        {miles(it.piezas)}
                      </span>
                      <span style={{ width: 44 }}>
                        <button
                          type="button"
                          title="Quitar la línea"
                          onClick={() => void quitarLinea(it.id, p)}
                          style={{ ...botonSecundario, width: 44, minHeight: 44, padding: 0, borderRadius: 999 }}
                        >
                          <Icono nombre="close" size={16} />
                        </button>
                      </span>
                    </motion.div>
                  ))}
                  </AnimatePresence>
                </div>
              </div>
              )}
            </motion.div>
          ))}
          </AnimatePresence>
        )}
        <Paginador
          pagina={pagina.pagina}
          paginas={pagina.paginas}
          total={pagina.total}
          desde={pagina.desde}
          que="pallets"
          onIr={pagina.setPagina}
        />
      </div>
      {dialogoConfirmar}
    </div>
  );
}
